/* eslint-disable @typescript-eslint/no-use-before-define */
import { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { useWebContext } from '@/resources/hooks/useWebContext';
import { setShipmentPayment } from '@/routes/order';
import { getUniquePayment } from '@/routes/settings';
import { getOrderMinValue, OrderStatus } from '@/services/global';
import { IOrder, IUniquePayment } from '@/store/ducks/order/types';

import * as PaymentActions from '../../../../../store/ducks/payment/actions';
import FooterDefaultButton from '../footerDefaultButton/FooterDefaultButton';
import { GeneratingPaymentModal } from './components/GeneratingPaymentModal';
import { PaggpixPayment } from './components/PaggpixPaymentModal';
import { WaitingAttendanceModal } from './components/WaitingAttendanceModal';
import {
  btnTitle,
  buttonColorByType,
  ButtonType,
  isPaymentMethodSelected,
  openPaymentPage,
} from './utils';

const MissingDataModal = lazy(() => import('./components/MissingDataModal'));
const MinOrderAlertModal = lazy(
  () => import('@/resources/components/deliveryMinOrderAlert/MinOrderAlertModal'),
);

interface Props {
  setShow: (value: boolean) => void;
  show: boolean;
  setValidUniquePayment: (value: boolean) => void;
  isPaid: boolean;
  handleFinishOrder: () => boolean;
  forwardedOrderAlert: (alert: boolean) => void;
}

export function PaymentButton({
  setShow,
  show,
  isPaid,
  setValidUniquePayment,
  handleFinishOrder,
  forwardedOrderAlert,
}: Props) {
  const { configs, order } = useWebContext();
  const dispatch = useDispatch();

  const [uniquePayment, setUniquePayment] = useState<IUniquePayment | null>(null);
  const [showMissingData, setShowMissingData] = useState<boolean>(false);
  const [showMinOrderWarning, setShowMinOrderWarning] = useState<boolean>(false);
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [showPaggPixModal, setShowPaggPixModal] = useState<boolean>(false);
  const [disableButton, setDisableButton] = useState<boolean>(false);
  const [isRedirectPaymentMethod, setIsRedirectPaymentMethod] = useState<boolean>(false);
  const [showGeneratingPaymentModal, setShowGeneratingPaymentModal] = useState<boolean>(false);
  const [paymentGenerated, setPaymentGenerated] = useState<boolean>(false);
  const [buttonType, setButtonType] = useState<ButtonType>('Default');
  const [showWaitingAttendanceModal, setShowWaitingAttendanceModal] = useState<boolean>(false);

  const hasBlockedMedicine = order?.cart.products.some((item) => item.prescription_retention);

  const validPayments = order?.payments?.filter((p) => !p.deleted_at || p.status !== 'canceled');
  const UniqueOrLastPayment = uniquePayment
    ? validPayments?.find((p) => !p.deleted_at && p.integration_id === uniquePayment?.method.id)
    : validPayments
        ?.filter((p) => !p.deleted_at || p.status !== 'canceled')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  const showPaymentButton =
    !validPayments?.length ||
    (!validPayments.some((p) => p.type === 'custom-payment') &&
      !order?.is_paid &&
      !order?.integration_code);

  const handleButtonType: (isRedirect?: boolean) => ButtonType = (isRedirect) => {
    const redirect = isRedirect || isRedirectPaymentMethod;
    if (redirect && !paymentGenerated && validPayments?.length) {
      return 'Redirect';
    }
    if (
      validPayments?.length &&
      (paymentGenerated || uniquePayment || (UniqueOrLastPayment && redirect))
    ) {
      return 'link';
    }

    return 'Default';
  };

  function handleCommonPaymentClick(action: () => void) {
    dispatch(PaymentActions.setHasShownPaidAlert(false));
    if (!order) return;
    if (order.status === OrderStatus.WAITING_SELLER) {
      setShowWaitingAttendanceModal(true);
      return;
    }
    action();
  }

  const onClickPaymentButton: Record<ButtonType, () => void> = {
    Redirect: () => handleCommonPaymentClick(() => setShowGeneratingPaymentModal(true)),
    Default: handleShowModal,
    link: () => handleCommonPaymentClick(() => redirectToPaymentMethod(order)),
  };

  useEffect(() => {
    setDisableButton(false);
    if (!order) return;
    const payment = UniqueOrLastPayment;
    if (!payment) {
      if (configs.unique_payment) {
        setValidUniquePayment(false);
      }
      return;
    }

    const isRedirect =
      !payment?.integration.data.can_finish_unpaid && payment?.type !== 'custom-payment';
    setDisableButton(false);
    setButtonType(handleButtonType(isRedirect));
    setIsRedirectPaymentMethod(isRedirect);
  }, [order]);

  useEffect(() => {
    if (uniquePayment) {
      setButtonType(handleButtonType());
    }
  }, [uniquePayment]);

  useEffect(() => {
    if (order && !order.payments?.some((i) => i.status !== 'canceled')) {
      setButtonType(handleButtonType());
    }
    if (!order || !UniqueOrLastPayment || !UniqueOrLastPayment?.payment_plan_id) return;
    isPaymentMethodSelected(
      order,
      UniqueOrLastPayment?.integration.id,
      UniqueOrLastPayment?.payment_plan_id,
    );
  }, [order?.cart.total]);

  useEffect(() => {
    if (isPaid) {
      setShowPaggPixModal(false);
    }
  }, [isPaid]);

  useEffect(() => {
    if (!show) {
      setDisableButton(false);
    }
  }, [show]);

  useEffect(() => {
    if (!order) return;
    setButtonType(handleButtonType());
  }, [paymentGenerated]);

  const redirectToPayment = (isRedirect: boolean, payment: string) => {
    if (!isRedirect) return;

    openPaymentPage(payment);
  };

  function redirectToPaymentMethod(order: IOrder | null) {
    if (!order || !order.payments?.length) return;
    const payment = uniquePayment
      ? order.payments?.find((p) => !p.deleted_at && p.integration_id === uniquePayment?.method.id)
      : order.payments
          ?.filter((p) => !p.deleted_at || p.status !== 'canceled')
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

    const isRedirect =
      !payment?.integration.data.can_finish_unpaid && payment?.type !== 'custom-payment';
    if (payment) {
      redirectToPayment(isRedirect, payment.payment_url);
    }
  }

  const handleMissingData = () => {
    toast(
      'Para prosseguir com esse método de pagamento selecionado, é preciso preencher os dados necessários.',
      {
        type: 'warning',
        autoClose: 10000,
      },
    );

    setShowMissingData(true);
  };

  function setPaymentMethod() {
    if (!uniquePayment || !order || !order.shipment.address?.id) return;

    if (order.cart.items_total < uniquePayment.plan.min_value) {
      setShowMinOrderWarning(true);

      return;
    }

    setShipmentPayment(order.id, uniquePayment.method.id, uniquePayment.plan.id)
      .then((order: IOrder) => {
        const isPaggPix = !!order?.payments?.filter((p) => p.type === 'paggpix').length;

        const payment = order?.payments?.find(
          (p) => !p.deleted_at && p.integration_id === uniquePayment.method.id,
        );

        const isRedirect =
          !payment?.integration.data.can_finish_unpaid && payment?.type !== 'custom-payment';
        setIsRedirectPaymentMethod(isRedirect);

        if (isPaggPix) {
          setShowPaggPixModal(true);
        }
      })
      .catch((e) => {
        if (e?.response?.data?.type === 'missing_data') {
          handleMissingData();
          const errors = e?.response?.data?.error_fields ?? [];

          setErrorFields(errors);

          return;
        }

        console.error(e);
        toast('Ocorreu um erro ao adicionar o método de pagamento', { type: 'error' });
      });
  }

  function handleShowModal() {
    dispatch(PaymentActions.setHasShownPaidAlert(false));
    if (!order) return;
    setDisableButton(true);
    if (order.cart.items_total >= getOrderMinValue(order)) {
      if (configs.unique_payment && uniquePayment) {
        setPaymentMethod();

        return;
      }

      setShow(true);
    } else {
      setShowMinOrderWarning(true);
    }
  }

  useEffect(() => {
    if (!order || !configs.unique_payment || !order.shipment.type || !order?.shipment?.pickup_at)
      return;

    const type = order.shipment.type === 'delivery' ? 'delivery' : 'in_store';

    getUniquePayment(order.dst_shop_id, type).then((payment) => {
      setUniquePayment(payment || null);

      setValidUniquePayment(!!payment);
    });
  }, [order?.dst_shop_id, order?.shipment.type, order?.shipment?.pickup_at]);

  useEffect(() => {
    if (uniquePayment) {
      setPaymentMethod();
    }
  }, [uniquePayment]);

  return (
    <>
      {showPaymentButton && (
        <FooterDefaultButton
          isRedirectPaymentMethod={isRedirectPaymentMethod}
          handleFinishOrder={handleFinishOrder}
          title={btnTitle[buttonType]}
          onClick={onClickPaymentButton[buttonType]}
          hasBlockedMedicine={hasBlockedMedicine}
          showModal={setShow}
          backgroundColor={buttonColorByType[buttonType]}
          forwardedOrderAlert={forwardedOrderAlert}
          isDisabled={disableButton}
        />
      )}

      <PaggpixPayment
        show={showPaggPixModal}
        onHide={() => {
          setDisableButton(false);
          setShowPaggPixModal(false);
        }}
      />

      {uniquePayment && (
        <Suspense fallback={<div />}>
          <MissingDataModal
            show={showMissingData}
            setShow={setShowMissingData}
            selectedPayment={{
              paymentId: uniquePayment.method.id,
              planId: uniquePayment.plan.id,
            }}
            errorFields={errorFields}
          />
        </Suspense>
      )}

      <GeneratingPaymentModal
        show={showGeneratingPaymentModal}
        onHide={() => {
          setPaymentGenerated(true);
          setShowGeneratingPaymentModal(false);
        }}
        redirect={() => {
          setPaymentGenerated(true);
          setShowGeneratingPaymentModal(false);
          redirectToPaymentMethod(order);
        }}
      />

      <WaitingAttendanceModal
        show={showWaitingAttendanceModal}
        onHide={() => setShowWaitingAttendanceModal(false)}
      />

      <Suspense fallback={<div />}>
        <MinOrderAlertModal
          show={showMinOrderWarning}
          setShow={setShowMinOrderWarning}
          value={uniquePayment?.plan.min_value ?? 0}
        />
      </Suspense>
    </>
  );
}
